import { IPage, IPageView, PageType } from "../models";
import { createContext, useContext, useState } from "react"

import { PageMode } from "../../../base/types";
import { useAuth } from "../../../../modules/auth";

interface IPageContext {
    page?: IPage,
    pageView?: IPageView,
    setPage: (page: IPage, view: IPageView) => void,
    setPageView: (view: IPageView) => void,
    getFieldFilter: (field: string) => string | undefined,
    setFieldFilter: (field: string, value?: string) => void,
}

const context = createContext<IPageContext>({
    pageView: { pageMode: PageMode.View },
    setPage: () => { },
    setPageView: () => { },
    getFieldFilter: () => undefined,
    setFieldFilter: () => { }
})

export const PageContextProvider = ({ children, page: globalPage, pageView: globalPageView }: {
    children?: React.ReactNode, page: IPage, pageView: IPageView
}) => {
    const [page, setPage] = useState<IPage>(globalPage);
    const [pageView, setPageView] = useState<IPageView>(globalPageView);
    const { site } = useAuth()

    const resetPage = (page: IPage, pageView: IPageView) => {
        setPage(page)
        setPageView(pageView)

        if (page?.type === PageType.Card || page?.type === PageType.List)
            document.title = `${site?.title || 'Portal Framework'} | ${page?.title || page?.name}`;
    }

    const getFieldFilter = (field: string) => {
        if (!pageView || !pageView.clientFilters) {
            return undefined;
        }

        return pageView.clientFilters.find(p => p.field === field)?.filter;
    }

    const setFieldFilter = (field: string, filter?: string) => {
        if (!pageView) {
            if (!filter) return;

            setPageView({
                pageMode: PageMode.View,
                clientFilters: [{ field, filter }]
            })
        } else if (!pageView.clientFilters) {
            if (!filter) return;

            setPageView({
                ...pageView,
                clientFilters: [{ field, filter }]
            })
        } else {
            const resultFilters = pageView.clientFilters.filter(p => p.field !== field)
            if (filter) {
                setPageView({
                    ...pageView,
                    clientFilters: [...resultFilters, { field, filter }]
                })
            } else {
                setPageView({ ...pageView, clientFilters: resultFilters })
            }
        }
    }

    return (
        <context.Provider value={{
            page,
            setPage: resetPage,
            pageView,
            setPageView,
            getFieldFilter,
            setFieldFilter
        }}>
            {children}
        </context.Provider>
    )
}

export function usePageContext() {
    return useContext(context)
}